import { useParams } from "react-router-dom";

import { Button } from "~/components";
import { UserDetailRow } from "~/modals/User/UserDetailsModal/components/UserDetailRow";
import { MODAL_ROUTES, useNavigateModal } from "~/router";
import { useActivityQuery } from "~/services/user-events/actions";
import { ACTIVITY_OUTCOME } from "~/services/user-events/schemas";
import type { ModalProps } from "~/shared.types";
import { useCopyToClipboard } from "~/shared/hooks/useCopyToClipboard";
import { dateToUsFormat } from "~/shared/utils";
import { icons, Modal, type ModalAction } from "~/ui";
import { tw } from "~/utils";
import { DataChangesSection } from "./DataChangesSection";

export const ActivityDetailModal = ({ show, onClose }: ModalProps) => {
  const { activityId } = useParams<{ activityId: string }>();
  const { data: activity, isLoading, isError } = useActivityQuery(activityId);
  const navigateModal = useNavigateModal();
  const { copy } = useCopyToClipboard();

  const handleShareEvent = () =>
    copy(
      `${window.location.origin}${MODAL_ROUTES.activityDetails}/${activityId}`,
      "Link copied to clipboard",
    );

  const actions: ModalAction[] = [
    {
      label: "Share Event",
      onClick: handleShareEvent,
    },
  ];

  const renderBody = () => {
    if (isError) {
      return (
        <p className="text-sm text-gray-400">
          This event could not be loaded. It may have been removed or is no
          longer available.
        </p>
      );
    }

    if (isLoading || !activity) {
      return (
        <div className="flex h-40 items-center justify-center">
          <icons.outline.ArrowPathIcon
            className={tw("text-indigo size-8 animate-spin stroke-2")}
          />
        </div>
      );
    }

    const { subject, causer } = activity;
    const affectedUserName = subject
      ? `${subject.firstName} ${subject.lastName}`
      : "—";
    const performedByName = causer
      ? `${causer.firstName} ${causer.lastName}`
      : "—";

    return (
      <div className="flex flex-col gap-4 pb-4">
        <UserDetailRow field="Type" value={activity.activityCategory} />
        <UserDetailRow field="Event Type" value={activity.eventType} />
        <UserDetailRow
          field="Outcome"
          value={
            activity.outcome === ACTIVITY_OUTCOME.Success ? "Success" : "Failed"
          }
        />
        <UserDetailRow field="Action" value={activity.action ?? "—"} />
        <UserDetailRow field="Source" value={activity.source ?? "—"} />
        <UserDetailRow
          field="Outcome Detail"
          value={activity.outcomeDetail ?? "—"}
        />

        <DataChangesSection dataChanges={activity.dataChanges} />

        <UserDetailRow
          field="Affected User"
          value={
            <div className="flex flex-col items-start gap-2">
              {affectedUserName}
              {subject && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigateModal(`${MODAL_ROUTES.userDetails}/${subject.id}`)
                  }
                >
                  See Affected User Details
                </Button>
              )}
            </div>
          }
        />

        <UserDetailRow field="Performed By" value={performedByName} />
        <UserDetailRow field="IP Address" value={activity.ip ?? "—"} />
        <UserDetailRow field="Agent" value={activity.userAgent ?? "—"} />
        <UserDetailRow
          field="Occurred At"
          value={dateToUsFormat(activity.occurredAt)}
        />
      </div>
    );
  };

  return (
    <Modal
      className="gap-6"
      show={show}
      title="Event Details"
      onClose={onClose}
      // Only a loaded event can be shared; while loading or on error there is
      // nothing to copy, so Share Event is omitted.
      actions={activity ? actions : undefined}
    >
      {renderBody()}
    </Modal>
  );
};
